/* eslint-disable @typescript-eslint/no-explicit-any */
import { API_BASE_URL, authedFetch } from '@/utils/apiConfig'
import React, { createContext, useContext, useState, ReactNode } from 'react'
import { supabase } from '@/integrations/supabase/client'
import { CheatingEvent } from '@/utils/cheatEventUtils'
import { storeSimpleVideoRecord } from '@/utils/videoRecordStatus'
import { HEARTBEAT_REQUEST_TIMEOUT_MS } from '@/utils/heartbeat'

// Define types for our context
export type QuestionOption = {
  isCorrect: boolean
  id: string
  label: string
  text: string
}

export type Question = {
  id: string
  text: string
  choices: QuestionOption[]
  options: QuestionOption[]
  category?: string
  correctAnswerId?: string
  image_path?: string
}

export type CandidateResponse = {
  questionId: string
  questionText: string
  choiceId: string
  isCorrect?: boolean
  selectedText?: string
  correctText?: string
}

export type InterviewResult = {
  resultId: string
  candidateId: string
  candidateName: string
  timestamp: string
  responses: CandidateResponse[]
  score?: number
  cheatingSummary?: CheatingSummary
  videoUrl?: string
  testId: string
  totalCorrect: number
  totalQuestions: number
  //assignmentId means testId actually also it where stores in supabase
  assignmentId: string
  status: string
  assignmentDueDate: string
  total_cheat_events?: number | null
  attempt_count?: number
}

type CheatingSummary = {
  tabViolations: number
  cheatingDetected: boolean
  cheatingEvents: CheatingEvent[]
}

interface ExamResult {
  candidateId: string
  candidateName: string
  responses: CandidateResponse[]
  tabViolations: number
  cheatingDetected: boolean
  cheatingEvents: CheatingEvent[]
  duration: number // in seconds or minutes depending on your system
}

// Sample questions for initial state
const initialQuestions: Question[] = []

// Sample results with cheating context
const initialResults: InterviewResult[] = []

// Context type
type InterviewContextType = {
  questions: Question[]
  allQuestions: Question[]
  currentQuestionIndex: number
  responses: CandidateResponse[]
  results: InterviewResult[]
  candidateName: string
  apiKey: string
  isInterviewActive: boolean
  isListening: boolean
  isSpeaking: boolean

  // Methods
  setCandidateName: (name: string) => void
  setCurrentQuestionIndex: (idx: number) => void
  setApiKey: (key: string) => void
  setIsInterviewActive: (active: boolean) => void
  setIsListening: (listening: boolean) => void
  setIsSpeaking: (speaking: boolean) => void
  startInterview: () => void
  endInterview: () => void
  nextQuestion: () => void
  previousQuestion: () => void
  addResponse: (response: CandidateResponse) => void
  initializeResponses: (responses: CandidateResponse[]) => void
  submitInterview: (examResult: any) => Promise<string>
  addQuestion: (question: Omit<Question, 'id'>) => void
  updateQuestion: (question: Question) => void
  deleteQuestion: (id: string) => void
  importQuestions: (questions: Omit<Question, 'id'>[]) => void
  setQuestions: (questions: Question[]) => void
  saveInterviewVideo: (
    blob: Blob,
    assignment_id: string,
    test_id: string,
    name: string,
    type: string,
    companyId: string,
    options?: {
      is_random?: boolean
      attempt_number?: number
      candidateToken?: string
    }
  ) => Promise<string>
  saveResponse: (payload: {
    candidateId: string
    assignmentId: string
    testId: string
    questionId: string
    choiceId: string
    companyId: string
    candidateToken: string
  }) => Promise<void>
}

// Create context with default values
const InterviewContext = createContext<InterviewContextType>({
  questions: initialQuestions,
  allQuestions: initialQuestions,
  currentQuestionIndex: 0,
  responses: [],
  results: initialResults,
  candidateName: '',
  apiKey: '',
  isInterviewActive: false,
  isListening: false,
  isSpeaking: false,

  setCandidateName: () => {},
  setCurrentQuestionIndex: (idx: number) => {},
  setApiKey: () => {},
  setIsInterviewActive: () => {},
  setIsListening: () => {},
  setIsSpeaking: () => {},
  startInterview: () => {},
  endInterview: () => {},
  nextQuestion: () => {},
  previousQuestion: () => {},
  addResponse: () => {},
  initializeResponses: () => {},
  submitInterview: () => Promise.resolve(''),
  addQuestion: () => {},
  updateQuestion: () => {},
  deleteQuestion: () => {},
  importQuestions: () => {},
  setQuestions: () => {},
  saveInterviewVideo: () => Promise.resolve(''),
  saveResponse: async () => {},
})

// Provider component
export const InterviewProvider = ({ children }: { children: ReactNode }) => {
  const [questions, setQuestions] = useState<Question[]>(initialQuestions)
  const [allQuestions, setAllQuestions] = useState<Question[]>(initialQuestions)
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0)
  const [responses, setResponses] = useState<CandidateResponse[]>([])
  const [results, setResults] = useState<InterviewResult[]>(initialResults)
  const [candidateName, setCandidateName] = useState('')
  const [apiKey, setApiKey] = useState('')
  const [isInterviewActive, setIsInterviewActive] = useState(false)
  const [isListening, setIsListening] = useState(false)
  const [isSpeaking, setIsSpeaking] = useState(false)

  const updateResultWithVideo = (id: string, videoUrl: string) => {
    setResults((prevResults) =>
      prevResults.map((result) => (result.resultId === id ? { ...result, videoUrl } : result))
    )
  }

  async function saveInterviewVideo(
    blob: Blob,
    assignment_id: string,
    test_id: string,
    name: string,
    type: string,
    companyId: string,
    options?: {
      is_random?: boolean
      attempt_number?: number
      candidateToken?: string
    }
  ) {
    if (!blob || !assignment_id || !test_id) {
      console.error('Missing blob or assignment details.')
      return
    }

    const attemptN = options?.attempt_number ?? 1
    const filePath = `${test_id}/${assignment_id}/attempt_${attemptN}/${name}.webm`

    try {
      if (!options?.candidateToken) throw new Error('Candidate session required for video upload')
      const signedResponse = await fetch(`${API_BASE_URL}/api/video-uploads/sign-simple`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          assignmentId: assignment_id,
          candidateToken: options.candidateToken,
          key: filePath,
        }),
      })
      if (!signedResponse.ok) throw new Error('Could not authorize video upload')
      const { token } = await signedResponse.json()

      const { error: uploadError } = await supabase.storage
        .from('video-recordings')
        .uploadToSignedUrl(filePath, token, blob, { upsert: true })

      if (uploadError) {
        console.error('Error uploading video:', uploadError)
        return
      }

      // Store the object path so access can be granted only when an admin requests playback.
      const videoId = await storeSimpleVideoRecord(
        assignment_id,
        filePath,
        type,
        companyId,
        options
      )
      return videoId || ''
    } catch (err) {
      console.error('An unexpected error occurred during video processing:', err)
      return ''
    }
  }

  // Start the interview
  const startInterview = () => {
    setCurrentQuestionIndex(0)
    setResponses([])
    setIsInterviewActive(true)
  }

  // End the interview
  const endInterview = () => {
    setIsInterviewActive(false)
  }

  // Navigate to next question
  const nextQuestion = () => {
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1)
    }
  }

  // Navigate to previous question
  const previousQuestion = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1)
    }
  }

  async function saveResponse(payload: {
    candidateId: string
    assignmentId: string
    testId: string
    questionId: string
    choiceId: string
    companyId: string
    candidateToken: string
  }) {
    const res = await fetch(`${API_BASE_URL}/api/save_response`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      signal: AbortSignal.timeout(HEARTBEAT_REQUEST_TIMEOUT_MS),
    })
    if (!res.ok) throw new Error(`Failed to save response: ${res.status}`)
  }

  // Add a response
  const addResponse = (response: CandidateResponse) => {
    setResponses((prev) => {
      // Check if response already exists
      const exists = prev.some((r) => r.questionId === response.questionId)
      if (exists) {
        // Replace existing response
        return prev.map((r) => (r.questionId === response.questionId ? response : r))
      } else {
        // Add new response
        return [...prev, response]
      }
    })
  }

  // Pre-populate responses from previously saved answers (used on renewal re-entry)
  const initializeResponses = (initial: CandidateResponse[]) => {
    setResponses(initial)
  }

  // Submit the interview
  async function submitInterview(examResultData: ExamResult): Promise<string> {
    const backendApiUrl = `${API_BASE_URL}/api/submit_results`

    const response = await fetch(backendApiUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(examResultData),
    })

    if (!response.ok) throw new Error(`Failed to submit interview: ${response.status}`)

    setResponses([])
    setCurrentQuestionIndex(0)
    setIsInterviewActive(false)

    return Date.now().toString()
  }

  // Add a new question
  const addQuestion = (question: Omit<Question, 'id'>) => {
    const newQuestion: Question = {
      ...question,
      id: Date.now().toString(),
    }
    setAllQuestions((prev) => [...prev, newQuestion])
  }

  // Update an existing question
  const updateQuestion = (question: Question) => {
    setAllQuestions((prev) => prev.map((q) => (q.id === question.id ? question : q)))
  }

  // Delete a question
  const deleteQuestion = (id: string) => {
    setAllQuestions((prev) => prev.filter((q) => q.id !== id))
  }

  // Import multiple questions
  const importQuestions = (newQuestions: Omit<Question, 'id'>[]) => {
    const questionsWithIds = newQuestions.map((question, index) => ({
      ...question,
      id: (Date.now() + index).toString(),
    }))

    setAllQuestions((prev) => [...prev, ...questionsWithIds])
  }

  const value = {
    questions,
    allQuestions,
    currentQuestionIndex,
    responses,
    results,
    candidateName,
    apiKey,
    isInterviewActive,
    isListening,
    isSpeaking,

    setCandidateName,
    setCurrentQuestionIndex,
    setApiKey,
    setIsInterviewActive,
    setIsListening,
    setIsSpeaking,
    startInterview,
    endInterview,
    nextQuestion,
    previousQuestion,
    addResponse,
    initializeResponses,
    submitInterview,
    addQuestion,
    updateQuestion,
    deleteQuestion,
    importQuestions,
    setQuestions,
    saveInterviewVideo,
    saveResponse,
  }

  return <InterviewContext.Provider value={value}>{children}</InterviewContext.Provider>
}

// Custom hook for using the context
export const useInterview = () => {
  const context = useContext(InterviewContext)
  if (context === undefined) {
    throw new Error('useInterview must be used within an InterviewProvider')
  }
  return context
}
